import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, orderBy, query, setDoc } from 'firebase/firestore';
import { toast } from 'sonner';
import { Ban, EyeOff, Lock, LogIn } from 'lucide-react';

import { db } from '../../firebase/config';
import { AuthContext } from '../../contexts/AuthContext';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { cn } from '../../lib/utils';
import RatingScale from './tournament/RatingScale';

const ADMIN_CATEGORIES = [
  { key: 'gameplay', label: 'Gameplay' },
  { key: 'visuals', label: 'Visuals' },
  { key: 'theme', label: 'Theme' },
  { key: 'fun', label: 'Fun' },
];

const rankClass = { 1: 'text-gold', 2: 'text-silver', 3: 'text-bronze' };

const SectionHeader = ({ title, description }) => (
  <div>
    <h2 className="font-display text-xl font-semibold">{title}</h2>
    {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
  </div>
);

const Notice = ({ icon: Icon, tone = 'muted', children }) => (
  <div
    className={cn(
      'flex items-center gap-2.5 rounded-md border px-3.5 py-2.5 text-sm',
      tone === 'warning'
        ? 'border-warning/40 text-warning'
        : 'border-border/60 text-muted-foreground'
    )}
  >
    <Icon className="h-4 w-4 shrink-0" />
    <span>{children}</span>
  </div>
);

const ScoreBar = ({ label, value, accent = false }) => (
  <div>
    <div className="flex items-baseline justify-between gap-2">
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
        {label}
      </span>
      <span className="font-mono text-xs font-semibold tabular-nums">{value.toFixed(1)}</span>
    </div>
    <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted">
      <div
        className={cn('h-full rounded-full', accent ? 'bg-heat' : 'bg-primary')}
        style={{ width: `${(Math.min(value, 10) / 10) * 100}%` }}
      />
    </div>
  </div>
);

const HiddenScoreBar = ({ label }) => (
  <div>
    <div className="flex items-baseline justify-between gap-2">
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
        {label}
      </span>
      <EyeOff className="h-3 w-3 text-muted-foreground/60" />
    </div>
    <div className="mt-1.5 h-1.5 rounded-full border border-dashed border-border" />
  </div>
);

const CommunityAverage = ({ visible, average, count }) => {
  if (!visible) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <EyeOff className="h-3.5 w-3.5" />
        Average hidden until results are revealed.
      </span>
    );
  }
  if (count === 0) {
    return <span>No community votes yet.</span>;
  }
  return (
    <span>
      Community average{' '}
      <span className="font-mono font-semibold tabular-nums text-foreground">
        {average.toFixed(1)}
      </span>
      /10 · {count} vote{count !== 1 ? 's' : ''}
    </span>
  );
};

const TeamColorDot = ({ color }) => (
  <span
    aria-hidden
    className="h-3 w-3 shrink-0 rounded-full border border-border/60"
    style={{ backgroundColor: color || 'hsl(var(--muted))' }}
  />
);

const MappingTournamentView = ({ tournament, tournamentId }) => {
  const [teams, setTeams] = useState([]);
  const [teamScores, setTeamScores] = useState({});
  const [communityVotes, setCommunityVotes] = useState({});
  const [userVotes, setUserVotes] = useState({});
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(false);
  const [loginDialog, setLoginDialog] = useState(false);

  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);

  // Voting is open unless the admin has explicitly closed it.
  const isVotingOpen = tournament?.votingOpen !== false;
  // The community average stays hidden until the admin reveals it. Firestore
  // rules only allow listing the votes collection once this flag is true.
  const averagesVisible = tournament?.communityAverageVisible === true;

  const fetchMappingData = useCallback(async () => {
    try {
      const teamsQuery = query(
        collection(db, 'tournaments', tournamentId, 'teams'),
        orderBy('name')
      );
      const teamsSnapshot = await getDocs(teamsQuery);
      const teamsData = teamsSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

      const scoresSnapshot = await getDocs(
        collection(db, 'tournaments', tournamentId, 'teamScores')
      );
      const scoresData = {};
      scoresSnapshot.docs.forEach((d) => {
        scoresData[d.data().teamId] = d.data().scores;
      });

      // Community averages are only readable (and only shown) once revealed.
      const communityScores = {};
      if (averagesVisible) {
        try {
          const votesSnapshot = await getDocs(
            collection(db, 'tournaments', tournamentId, 'communityVotes')
          );
          const allVotes = {};
          votesSnapshot.docs.forEach((d) => {
            Object.entries(d.data().votes || {}).forEach(([teamId, rating]) => {
              if (!allVotes[teamId]) allVotes[teamId] = [];
              allVotes[teamId].push(rating);
            });
          });
          Object.entries(allVotes).forEach(([teamId, ratings]) => {
            communityScores[teamId] = {
              average: ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length,
              count: ratings.length,
            };
          });
        } catch (error) {
          console.error('Error fetching community votes:', error);
        }
      }

      const playersSnapshot = await getDocs(collection(db, 'tournaments', tournamentId, 'players'));
      const playersData = playersSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

      setTeams(teamsData);
      setTeamScores(scoresData);
      setCommunityVotes(communityScores);
      setPlayers(playersData);
    } catch (error) {
      console.error('Error fetching mapping tournament data:', error);
    } finally {
      setLoading(false);
    }
  }, [tournamentId, averagesVisible]);

  const fetchUserVotes = useCallback(async () => {
    if (!currentUser) return;
    try {
      const userVoteDoc = await getDoc(
        doc(db, 'tournaments', tournamentId, 'communityVotes', currentUser.uid)
      );
      if (userVoteDoc.exists()) {
        setUserVotes(userVoteDoc.data().votes || {});
      }
    } catch (error) {
      console.error('Error fetching user votes:', error);
    }
  }, [currentUser, tournamentId]);

  useEffect(() => {
    fetchMappingData();
  }, [fetchMappingData]);

  useEffect(() => {
    fetchUserVotes();
  }, [fetchUserVotes]);

  const isCompetitor =
    !!currentUser &&
    players.some(
      (player) =>
        player.userId === currentUser.uid ||
        player.uid === currentUser.uid ||
        player.id === currentUser.uid
    );

  const handleVote = async (teamId, rating) => {
    if (!currentUser) {
      setLoginDialog(true);
      return;
    }
    if (isCompetitor) {
      toast.warning('Competitors cannot vote in the tournament they are participating in.');
      return;
    }
    if (!isVotingOpen) {
      toast.warning('Community voting is closed for this tournament.');
      return;
    }

    setVoting(true);
    try {
      const newVotes = { ...userVotes, [teamId]: rating };
      await setDoc(
        doc(db, 'tournaments', tournamentId, 'communityVotes', currentUser.uid),
        {
          userId: currentUser.uid,
          username: currentUser.displayName || currentUser.email,
          votes: newVotes,
          updatedAt: new Date(),
        },
        { merge: true }
      );
      setUserVotes(newVotes);
      toast.success('Vote submitted.');
      if (averagesVisible) await fetchMappingData();
    } catch (error) {
      console.error('Error submitting vote:', error);
      toast.error('Could not submit your vote. Please try again.');
    } finally {
      setVoting(false);
    }
  };

  // While results are hidden the leaderboard must not leak the standings, so
  // teams are shown in a random order (stable per page load) instead of by
  // score. Fisher–Yates over the team IDs.
  const shuffledOrder = useMemo(() => {
    const ids = teams.map((team) => team.id);
    for (let i = ids.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [ids[i], ids[j]] = [ids[j], ids[i]];
    }
    return ids;
  }, [teams]);

  const byScore = (a, b) => b.totalScore - a.totalScore;
  const byShuffledOrder = (a, b) => shuffledOrder.indexOf(a.id) - shuffledOrder.indexOf(b.id);

  const rankedTeams = teams
    .map((team) => {
      const admin = teamScores[team.id] || {};
      const scores = {
        gameplay: admin.gameplay || 0,
        visuals: admin.visuals || 0,
        theme: admin.theme || 0,
        fun: admin.fun || 0,
        community: averagesVisible ? communityVotes[team.id]?.average || 0 : 0,
      };
      return {
        ...team,
        scores,
        totalScore: scores.gameplay + scores.visuals + scores.theme + scores.fun + scores.community,
        communityData: communityVotes[team.id] || { average: 0, count: 0 },
      };
    })
    .sort(averagesVisible ? byScore : byShuffledOrder);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-6 w-48 animate-pulse rounded-md bg-muted" />
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-28 animate-pulse rounded-md bg-muted/60" />
        ))}
      </div>
    );
  }

  if (teams.length === 0) {
    return (
      <div className="rounded-md border border-border/60 py-16 text-center">
        <p className="text-sm font-medium">No teams yet</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Teams will appear here once they are added to this mapping contest.
        </p>
      </div>
    );
  }

  const canVote = !!currentUser && !isCompetitor && isVotingOpen;

  return (
    <div className="space-y-10">
      {/* Status notices */}
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        {!currentUser && isVotingOpen && (
          <Notice icon={LogIn}>Log in to rate your favorite teams.</Notice>
        )}
        {isCompetitor && (
          <Notice icon={Ban} tone="warning">
            As a competitor in this tournament, you cannot vote for teams.
          </Notice>
        )}
        {!isVotingOpen && (
          <Notice icon={Lock}>Community voting is closed for this tournament.</Notice>
        )}
        {!averagesVisible && (
          <Notice icon={EyeOff}>
            Community averages are hidden until the admins reveal the results.
            {currentUser ? ' Your own votes stay visible to you.' : ''}
          </Notice>
        )}
      </div>

      {/* Leaderboard */}
      <section className="space-y-4">
        <SectionHeader
          title={averagesVisible ? 'Leaderboard' : 'Entries'}
          description={
            averagesVisible
              ? 'Teams ranked by total score across all categories, including the community rating.'
              : 'Shown in random order until the results are revealed — no peeking at the standings.'
          }
        />

        <div className="space-y-3">
          {rankedTeams.map((team, index) => {
            const rank = index + 1;
            return (
              <Card
                key={team.id}
                className="border-l-2 shadow-sm"
                style={{ borderLeftColor: team.color || undefined }}
              >
                <CardContent className="p-4 sm:p-5">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex min-w-0 items-center gap-3">
                      {averagesVisible && (
                        <span
                          className={cn(
                            'font-mono text-lg font-semibold tabular-nums',
                            rankClass[rank] || 'text-muted-foreground'
                          )}
                        >
                          {String(rank).padStart(2, '0')}
                        </span>
                      )}
                      <TeamColorDot color={team.color} />
                      <h3 className="truncate font-display text-lg font-semibold">{team.name}</h3>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-2xl font-semibold tabular-nums">
                        {team.totalScore.toFixed(1)}
                      </div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                        Points
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3 lg:grid-cols-5">
                    {ADMIN_CATEGORIES.map(({ key, label }) => (
                      <ScoreBar key={key} label={label} value={team.scores[key]} />
                    ))}
                    {averagesVisible ? (
                      <ScoreBar label="Community" value={team.scores.community} accent />
                    ) : (
                      <HiddenScoreBar label="Community" />
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Community voting */}
      <section className="space-y-4">
        <SectionHeader
          title="Community voting"
          description={
            isVotingOpen
              ? 'Rate each team from 1 to 10. You can change your vote while voting is open.'
              : 'Voting is closed — these are the votes you cast.'
          }
        />

        <div className="grid gap-3 md:grid-cols-2">
          {rankedTeams.map((team) => {
            const ownVote = userVotes[team.id] || 0;
            return (
              <Card key={team.id} className="shadow-sm">
                <CardContent className="p-4 sm:p-5">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-2.5">
                      <TeamColorDot color={team.color} />
                      <h3 className="truncate font-display text-base font-semibold">{team.name}</h3>
                    </div>
                    {ownVote > 0 && (
                      <Badge variant="outline" className="shrink-0 border-heat/60 text-heat">
                        Your vote: {ownVote}/10
                      </Badge>
                    )}
                  </div>

                  <div className="mt-4">
                    <RatingScale
                      value={ownVote}
                      onChange={(rating) => handleVote(team.id, rating)}
                      disabled={!canVote || voting}
                    />
                  </div>

                  <div className="mt-3 flex items-center justify-between gap-3 text-sm text-muted-foreground">
                    <CommunityAverage
                      visible={averagesVisible}
                      average={team.communityData.average}
                      count={team.communityData.count}
                    />

                    {!currentUser && isVotingOpen && (
                      <Button variant="outline" size="sm" onClick={() => setLoginDialog(true)}>
                        Log in to vote
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Login dialog */}
      <Dialog open={loginDialog} onOpenChange={setLoginDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log in required</DialogTitle>
            <DialogDescription>
              You need to be logged in to vote for teams. Log in to take part in the community
              rating.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setLoginDialog(false)}>
              Cancel
            </Button>
            <Button onClick={() => navigate('/login')}>Go to Login</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MappingTournamentView;
